import React, { createRef, memo, RefObject, useEffect, useState } from 'react';
import { styled } from '@nykaa/ui-components';

interface Props {
  children: React.ReactNode;
  content: React.FC;
  isHidden?: boolean;
  style?: {
    [key: string]: string | number;
  };
  isDimissableTooltip?: boolean;
}
interface ChildWrapperProps {
  showTooltip: boolean;
  position: number;
  arrowStyles: {
    [key: string]: string;
  };
  isDimissableTooltip: boolean;
}

interface TooltipWrapperProps {
  hasAdjusted: boolean;
  tooltipPosition: number;
}

const Wrapper = styled.div<ChildWrapperProps>`
  position: relative;
  &::after {
    content: ${({ showTooltip }) => (showTooltip ? '""' : '')};
    left: ${({ position }) => `${position}px` || 0};
    background: ${({ arrowStyles }) => arrowStyles.background};
    position: absolute;
    bottom: -9px;
    width: 15px;
    height: 15px;
    transform: rotate(225deg);
    border-color: ${({ arrowStyles }) => arrowStyles.borderColor};
    border-width: ${({ arrowStyles }) => arrowStyles.borderWidth};
    border-style: ${({ arrowStyles }) => arrowStyles.borderStyle};
    z-index: 11;
    border-left: none;
    border-top: none;
    ${({ isDimissableTooltip }) => isDimissableTooltip && 'border-radius: 2px'};
  }
`;

const TooltipWrapper = styled.div<TooltipWrapperProps>`
  opacity: ${({ hasAdjusted }) => Number(hasAdjusted)};
  border-radius: ${({ theme }) => theme.borders.radius30};
  margin: ${({ theme: { spacing } }) =>
    `${spacing.spacing0} ${spacing.spacing40} ${spacing.spacing40} ${spacing.spacing40}`};
  transform: translate(${({ tooltipPosition }) => Number(tooltipPosition)}px);
  background-color: rgba(0, 19, 37, 1);
  position: absolute;
  z-index: 10;
`;

function Tooltip({
  children,
  content: TooltipComponent,
  isHidden,
  style = {},
  isDimissableTooltip = false,
}: Props) {
  const [tooltipPosition, setTooltipPosition] = useState(0);
  const [childPosition, setChildPosition] = useState(0);
  // This is defined so that tooltip is shown only when it's positions are set
  const [hasAdjusted, setHasAdjusted] = useState(false);
  // Refs
  const tooltipRef = createRef();
  const childRef = createRef();

  useEffect(() => {
    // Set Positions for tooltip component
    if (tooltipRef?.current && !hasAdjusted) {
      const tooltipElem = (
        tooltipRef as RefObject<HTMLDivElement>
      )?.current?.getBoundingClientRect();
      const marginRight = 8;
      let { x = 0, width = 0 } = tooltipElem || { width: 0, x: 0 };
      let requiredWidth = x + width + marginRight;
      let { width: availableWidth } = document.body.getClientRects()[0];
      let spaceNeed = availableWidth - requiredWidth;
      if (spaceNeed < 0) {
        setTooltipPosition(spaceNeed);
      }
      setHasAdjusted(true);
    }
  }, [tooltipRef, hasAdjusted, tooltipPosition]);

  useEffect(() => {
    const childElem = (
      childRef as RefObject<HTMLDivElement>
    )?.current?.getBoundingClientRect();
    let { width = 0 } = childElem || { width: 0 };
    const left = width / 2 - 8;
    if (left !== childPosition) {
      setChildPosition(left);
    }
  }, [childRef, childPosition]);

  const shouldShow = !isHidden && hasAdjusted;
  const arrowStyles = {
    ...style,
    background: style.backgroundColor?.toString() || 'rgba(0,19,37,1)',
  };

  useEffect(() => {
    if (isHidden && hasAdjusted) {
      setHasAdjusted(false);
    }
  }, [hasAdjusted, isHidden]);
  return (
    <div>
      <Wrapper
        showTooltip={shouldShow}
        ref={childRef as RefObject<HTMLDivElement>}
        position={childPosition}
        arrowStyles={arrowStyles}
        isDimissableTooltip={isDimissableTooltip}
      >
        {children}
      </Wrapper>
      {!isHidden && (
        <TooltipWrapper
          style={style}
          hasAdjusted={hasAdjusted}
          tooltipPosition={tooltipPosition}
          ref={tooltipRef as RefObject<HTMLDivElement>}
        >
          <TooltipComponent />
        </TooltipWrapper>
      )}
    </div>
  );
}

export default memo(Tooltip);
